import { Component, OnDestroy, ViewChild } from '@angular/core';
import { AsgardFeatureNames } from 'src/models/AsgardFeatures';
import { DocumentDrawingToolsService } from 'src/app/services/document/document-drawing-tools.service';
import { Subscription } from 'rxjs';
import { TabService } from 'src/app/services/tab.service';
import { AsgardTab } from 'src/app/core/asgard-tab';
import { PdfToolType, PdfStrokeColor } from 'src/app/core/pdf-utils/enums';
import {
  PdfStrokeColors
} from 'src/app/core/pdf-utils/drawing-utils';
import { IToolStyles } from 'src/app/core/pdf-utils/interfaces';
import { DocumentManagerService } from 'src/app/services/document/document-manager.service';
import { AsgardDocPdf } from 'src/app/core/documents/asgard-doc-pdf';
import { CurrentUiStoreService } from 'src/app/services/ui/current-ui-store.service';
import { FloatingMenuComponent } from '../../floating-menu/floating-menu.component';
import { CurrentUIState } from 'src/models/UIState';
import { MatDividerModule } from '@angular/material/divider';
import { NgClass, NgStyle } from '@angular/common';
import { TooltipDirective } from '../../../utils/directives/tooltip.directive';
import { Core } from '@pdftron/webviewer';
import { GoogleAnalyticsActions, GoogleAnalyticsService } from 'src/app/services/google-analytics.service';


@Component({
    selector: 'asg-drawing-tools-floating-menu',
    templateUrl: './drawing-tools-floating-menu.component.html',
    styleUrls: ['./drawing-tools-floating-menu.component.scss'],
    standalone: true,
    imports: [FloatingMenuComponent, TooltipDirective, NgClass, MatDividerModule, NgStyle]
})
export class DrawingToolsFloatingMenuComponent implements OnDestroy {
  @ViewChild(FloatingMenuComponent) floatingMenu: FloatingMenuComponent;

  private readonly subscriptions: Subscription = new Subscription();
  private selectedAnnotations: Core.Annotations.MarkupAnnotation[] = [];

  public uiState: CurrentUIState & { [key: string]: any } | null = null;
  public isShapeSelected: boolean = false;
  public activeShapeType: PdfToolType = PdfToolType.Rectangle;
  public activeShapeStyles: IToolStyles;
  public annotationsVisibility: { [key: number]: boolean } = {};
  public drawingsDefaultVisibility: boolean;
  public strokeColors: PdfStrokeColor[] = PdfStrokeColors.concat();
  public strokeThicknessStyles: Array<{ title: string, value: number, class: string }> = [
    { title: '1pt', value: 1.0, class: 'one_pt' },
    { title: '2pt', value: 2.0, class: 'two_pt' },
    { title: '3pt', value: 3.0, class: 'three_pt' },
    { title: '4pt', value: 4.0, class: 'four_pt' },
    { title: '5pt', value: 5.0, class: 'five_pt' }
  ];

  public strokeColorValue: PdfStrokeColor | undefined;
  public strokeThicknessValue: number | undefined;
  public AsgardFeatureNames: typeof AsgardFeatureNames = AsgardFeatureNames;
  public PdfToolType: typeof PdfToolType = PdfToolType;

  constructor(
    private readonly docMgrService: DocumentManagerService,
    private drawingToolsService: DocumentDrawingToolsService,
    private tabService: TabService,
    private uiService: CurrentUiStoreService,
    private readonly googleAnalyticsService: GoogleAnalyticsService
  ) {
    this.initSubscriptions();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  close(): void {
    this.floatingMenu.close();
    this.floatingMenu.hide();
  }

  private initSubscriptions(): void {
    this.subscriptions.add(
      this.drawingToolsService
        .getShapeStylesSubject()
        .subscribe((s: IToolStyles) => {
          this.activeShapeStyles = s;
          this.recalculateStyleValues();
        })
    );
    this.subscriptions.add(
      this.drawingToolsService
        .getDrawingsDefaultVisibilitySubject()
        .subscribe(drawingsDefaultVisibility => this.drawingsDefaultVisibility = drawingsDefaultVisibility)
    );
    this.subscriptions.add(
      this.drawingToolsService
        .getSelectedShapesSubject()
        .subscribe(selectedAnnotations => {
          this.selectedAnnotations = selectedAnnotations;
          this.recalculateStyleValues();
        })
    );
    this.subscriptions.add(
      this.uiService.getUIState()
        .subscribe((e: CurrentUIState) => {
          this.uiState = e as CurrentUIState;
        })
    );

    this.subscriptions.add(
      this.tabService
        .activeTabObservable()
        .subscribe((activeTab: AsgardTab | null) => {
          if (!activeTab) {
            return;
          }
          if (!(this.docMgrService.currentDoc instanceof AsgardDocPdf)) {
            this.uiService.setItemUnActive(AsgardFeatureNames.DRAWING_TOOLS);
          }
          if (this.annotationsVisibility[activeTab.id] === undefined) {
            // new tab
            this.annotationsVisibility[
              activeTab.id
            ] = this.drawingsDefaultVisibility;
          }
          this.handleDrawingsVisibilityChange(this.annotationsVisibility[activeTab.id]);
          this.isShapeSelected = false;
          this.drawingToolsService.exitDrawMode();
        })
    );

    this.subscriptions.add(this.drawingToolsService.drawModeActiveObservable.subscribe(s => {
      this.isShapeSelected = s;
    }));
  }

  private recalculateStyleValues(): void {
    this.strokeColorValue = this.calcStrokeColorValue();
    this.strokeThicknessValue = this.calcStrokeThicknessValue();
  }

  private calcStrokeColorValue(): PdfStrokeColor | undefined {
    if (!this.selectedAnnotations || !this.selectedAnnotations.length) {
      return this.activeShapeStyles.strokeColor;
    }
    const annotationColor = this.selectedAnnotations[0].StrokeColor.toHexString();
    return annotationColor as PdfStrokeColor;
  }

  private calcStrokeThicknessValue(): number | undefined {
    if (!this.selectedAnnotations || !this.selectedAnnotations.length) {
      return this.activeShapeStyles.strokeThickness;
    }
    return this.selectedAnnotations[0].StrokeThickness;
  }

  private onStyleSelected(style: Partial<IToolStyles>): void {
    if (this.selectedAnnotations && this.selectedAnnotations.length) {
      this.selectedAnnotations.forEach(a =>
        this.drawingToolsService.updateAnnotationStyles(a, style)
      );
    } else {
      this.drawingToolsService.updateShapeStyles(this.activeShapeType, style);
    }
    this.recalculateStyleValues();
  }

  public get annotationsVisible(): boolean {
    return (
      !!this.annotationsVisibility &&
      !!this.tabService.activeTab &&
      !!this.tabService.activeTab.id &&
      this.annotationsVisibility[this.tabService.activeTab.id]
    );
  }

  public get hasActiveTab(): boolean {
    return (
      !!this.tabService.activeTab &&
      !!this.tabService.activeTab.id &&
      this.docMgrService.currentDoc instanceof AsgardDocPdf
    );
  }

  public toggleShapeType(shapeType: PdfToolType): void {
    if (!this.isShapeActive(shapeType)) {
      this.activeShapeType = shapeType;
      setTimeout(() => this.drawingToolsService.enterDrawMode(shapeType), 50);
      this.googleAnalyticsService.trackAction(GoogleAnalyticsActions.DRAWING_TOOLS, `${shapeType}`);
    } else {
      this.drawingToolsService.exitDrawMode();
    }
  }

  public toggleAnnotationsVisibility(): void {
    const { activeTab } = this.tabService;

    if (activeTab) {
      const isVisible = !this.annotationsVisibility[activeTab.id];
      this.annotationsVisibility[activeTab.id] = isVisible;

      this.handleDrawingsVisibilityChange(isVisible);
    }
  }

  private handleDrawingsVisibilityChange(isVisible: boolean) {
    const toggledFeatures: AsgardFeatureNames[] = [
      AsgardFeatureNames.DRAW_RECT,
      AsgardFeatureNames.DRAW_CIRCLE,
      AsgardFeatureNames.DRAW_ARROW,
      AsgardFeatureNames.DRAW_STR_LINE,
      AsgardFeatureNames.DRAW_LINE
    ];

    if (isVisible) {
      this.drawingToolsService.showAnnotations();
      this.uiService.enableState(toggledFeatures);
    } else {
      this.drawingToolsService.hideAnnotations();
      this.uiService.disableState(toggledFeatures);
      this.drawingToolsService.exitDrawMode();
    } 
  }

  public isShapeActive(shapeType: PdfToolType): boolean {
    return this.isShapeSelected && this.activeShapeType === shapeType;
  }

  public colorSelected(color: PdfStrokeColor): void {
    const style: Partial<IToolStyles> = {
      strokeColor: color
    };
    this.onStyleSelected(style);
  }

  public strokeThicknessSelected(strokeThickness: number): void {
    const style: Partial<IToolStyles> = {
      strokeThickness
    };
    this.onStyleSelected(style);
  }
}
